import type { TFunction } from 'i18next';
import type { Service, TimeSlot } from '@minibook/shared';
import type { ServiceWithCategory } from '../../api/bookingApi.js';
import { LanguageSwitcher } from '../../../app/components/LanguageSwitcher.js';
import { AppIcon } from '../../ui/Icon';
import { formatDuration, formatPrice, type PriceLocale } from '../../formatting.js';
import { ALL_CATEGORY_KEY } from '../bookingConstants.js';

type BookingStep1ServicesProps = {
  t: TFunction;
  priceLocale: PriceLocale;
  bookingFlowMode: 'services-first' | 'date-first';
  selectedSlot: TimeSlot | null;
  loading: boolean;
  searchQuery: string;
  selectedCategory: string;
  categories: string[];
  filteredServices: ServiceWithCategory[];
  selectedServices: Service[];
  canAddService: (service: Service) => boolean;
  formatSlotTime: (isoString: string | undefined | null) => string;
  onBack: () => void;
  onSearchChange: (value: string) => void;
  onCategoryChange: (category: string) => void;
  onToggleService: (service: Service) => void;
};

export function BookingStep1Services({
  t,
  priceLocale,
  bookingFlowMode,
  selectedSlot,
  loading,
  searchQuery,
  selectedCategory,
  categories,
  filteredServices,
  selectedServices,
  canAddService,
  formatSlotTime,
  onBack,
  onSearchChange,
  onCategoryChange,
  onToggleService,
}: BookingStep1ServicesProps) {
  return (
    <div className="booking-landing-card booking-services-card has-summary">
        <div
          className="booking-p-md-lg booking-gap-md booking-border-b"
          style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}
        >
          <button
            type="button"
            onClick={onBack}
            className="booking-icon-ghost"
            style={{ padding: 'var(--spacing-sm)', color: 'var(--color-text-primary)' }}
          >
            <AppIcon name="chevronLeft" size={24} />
          </button>
          <h1 className="booking-heading" style={{ margin: 0, flex: 1, textAlign: 'left' }}>
            {t('booking.selectServices')}
          </h1>
          <LanguageSwitcher />
        </div>

        {bookingFlowMode === 'date-first' && selectedSlot && selectedSlot.maxDurationMin != null && (
          <div
            className="booking-p-md-lg booking-border-b booking-color-muted booking-text-sm"
            style={{ flexShrink: 0 }}
          >
            <div>
              {t('booking.selectedTimeAvailable', {
                time: formatSlotTime(selectedSlot.startAt),
                minutes: selectedSlot.maxDurationMin,
              })}
            </div>
          </div>
        )}

        <div className="booking-p-md-lg booking-border-b" style={{ flexShrink: 0 }}>
          <div className="booking-search-inner">
            <AppIcon name="search" size={20} tone="muted" />
            <input
              type="search"
              inputMode="search"
              enterKeyHint="search"
              autoComplete="off"
              placeholder={t('booking.search')}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="booking-search-input"
            />
          </div>
        </div>

        <div
          className="booking-categories-scroll booking-p-md-lg booking-gap-sm booking-border-b"
          style={{ flexShrink: 0 }}
        >
          <button
            type="button"
            onClick={() => onCategoryChange(ALL_CATEGORY_KEY)}
            className={`booking-pill ${selectedCategory === ALL_CATEGORY_KEY ? 'booking-pill-active' : ''}`}
          >
            {t('booking.all')}
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => onCategoryChange(cat)}
              className={`booking-pill ${selectedCategory === cat ? 'booking-pill-active' : ''}`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="booking-services-list" style={{ flex: 1, overflowY: 'auto' }}>
          {loading && <div className="booking-loading">{t('booking.loadingServices')}</div>}

          {!loading && filteredServices.length === 0 && (
            <div className="booking-empty-state">
              {searchQuery ? t('booking.error.nothingFound') : t('booking.error.noServices')}
            </div>
          )}

          {filteredServices.map((service) => {
            const isSelected = selectedServices.some((s) => s.id === service.id);
            const disabledByTime =
              selectedSlot?.maxDurationMin != null && !canAddService(service) && !isSelected;
            const disabled = disabledByTime;
            return (
              <div
                key={service.id}
                onClick={() => !disabled && onToggleService(service)}
                className={`booking-service-item${disabled ? ' is-disabled' : ''}`}
              >
                <div className="booking-service-item__content">
                  <div className="booking-service-item__title">{service.name}</div>
                  <div className="booking-service-item__meta">
                    <span className="booking-service-item__duration">
                      {formatDuration(service.durationMin, priceLocale)}
                    </span>
                    {service.description && (
                      <span className="booking-service-item__description">{service.description}</span>
                    )}
                  </div>
                </div>
                <div className="booking-service-item__actions">
                  <div className="booking-service-item__price">
                    {formatPrice(Number(service.price), priceLocale)} {t('booking.currency')}
                  </div>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={disabled}
                    onChange={() => {}}
                    className="booking-service-item__checkbox"
                    tabIndex={disabled ? -1 : 0}
                  />
                </div>
              </div>
            );
          })}
        </div>
    </div>
  );
}
